import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import './Terminal.css'
import profileImg from '../../assets/Profile.jpg'
import { SESSION, EASTER_EGG_CMD } from './sessionConfig'
import { ToolsData } from '../../data/ToolsData'
import { ContactData } from '../../data/ContactData'

const PROMPT_USER = 'gabriel@site'
const MODE_STORAGE_KEY = 'terminal-mode'

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function displayLink(url) {
    if (url.startsWith('mailto:')) return url.slice(7)
    return url.replace(/^https?:\/\/(www\.)?/, '')
}

function linkifyToReact(text, links) {
    if (!links || !links.length) return text
    const found = links
        .map((link) => ({ ...link, idx: text.indexOf(link.text) }))
        .filter((link) => link.idx !== -1)
        .sort((a, b) => a.idx - b.idx)
    if (!found.length) return text

    const nodes = []
    let pos = 0
    found.forEach((link, i) => {
        if (link.idx > pos) nodes.push(text.slice(pos, link.idx))
        nodes.push(
            <a key={i} href={link.href} target="_blank" rel="noopener noreferrer">
                {link.text}
            </a>
        )
        pos = link.idx + link.text.length
    })
    if (pos < text.length) nodes.push(text.slice(pos))
    return nodes
}

const Terminal = ({ initialCommands }) => {
    const intl = useIntl()
    const t = useCallback((id, values) => intl.formatMessage({ id }, values), [intl])

    const reducedMotion =
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
            : false

    const [mode, setModeState] = useState(() => {
        try {
            const saved = localStorage.getItem(MODE_STORAGE_KEY)
            if (saved === 'auto' || saved === 'guided') return saved
        } catch (e) { /* localStorage unavailable */ }
        return 'guided'
    })
    const [booted, setBooted] = useState(false)
    const [bootLines, setBootLines] = useState([])
    const [entries, setEntries] = useState([])
    const [revealed, setRevealed] = useState({})
    const [historyList, setHistoryList] = useState([])
    const [, setHistoryIdx] = useState(-1)
    const [inputValue, setInputValue] = useState('')
    const [busy, setBusy] = useState(false)
    const [liveCmd, setLiveCmd] = useState('')
    const [autoDone, setAutoDone] = useState(false)

    const inputRef = useRef(null)
    const mountedRef = useRef(true)
    const autoGenRef = useRef(0)
    const entryKeyRef = useRef(0)
    const revealedRef = useRef({})
    revealedRef.current = revealed

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    function buildOutput(type) {
        if (type === 'whoami') {
            return {
                kind: 'whoami',
                text:
                    'gabriel alves\n' +
                    'role     : ' + t('TerminalWhoamiRole') + '\n' +
                    'location : ' + t('TerminalWhoamiLocation') + '\n' +
                    'focus    : ' + t('TerminalWhoamiFocus')
            }
        }
        if (type === 'about') {
            return {
                kind: 'text',
                text: [t('AboutDescription1'), t('AboutDescription2'), t('AboutDescription3')].join('\n\n')
            }
        }
        if (type === 'skills') {
            return { kind: 'badges', badges: ToolsData }
        }
        if (type === 'contact') {
            const links = ContactData.map((c) => ({ text: displayLink(c.link), href: c.link }))
            const lines = ContactData.map((c, i) => c.title.toLowerCase().padEnd(9, ' ') + ': ' + links[i].text)
            return { kind: 'links', text: t('ContactSubTitle') + '\n\n' + lines.join('\n'), links }
        }
        if (type === 'projects') {
            return { kind: 'text', text: t('ProjectBody') }
        }
        return { kind: 'text', text: '' }
    }

    // human-ish typing speed: irregular gaps between keystrokes, a pause around
    // spaces, and an occasional longer "hesitation" — not a metronome
    function humanTypeDelay(nextChar) {
        let delay = 65 + Math.random() * 95
        if (nextChar === ' ') delay += 70 + Math.random() * 90
        if (Math.random() < 0.08) delay += 220 + Math.random() * 380
        return delay
    }

    function typeLiveCommand(fullText, isCancelled) {
        return new Promise((resolve) => {
            if (reducedMotion) {
                if (mountedRef.current) setLiveCmd(fullText)
                resolve()
                return
            }
            let i = 0
            const tick = () => {
                if (!mountedRef.current || (isCancelled && isCancelled())) { resolve(); return }
                i++
                setLiveCmd(fullText.slice(0, i))
                if (i >= fullText.length) { resolve(); return }
                setTimeout(tick, humanTypeDelay(fullText[i]))
            }
            tick()
        })
    }

    async function appendCommandEntry(item, isCancelled) {
        const id = ++entryKeyRef.current
        const fullCmd = item.cmd
        const output = buildOutput(item.type)

        if (output.kind === 'badges') {
            if (!mountedRef.current) return
            setEntries((prev) => [...prev, { id, kind: 'command', cmd: fullCmd, cmdDisplay: fullCmd, output: { kind: 'loading', text: t('TerminalSkillsLoading') } }])
            await wait(reducedMotion ? 0 : 420)
            if ((isCancelled && isCancelled()) || !mountedRef.current) return
            setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, output: { kind: 'badges', badges: output.badges } } : e)))
            return
        }

        if (!mountedRef.current) return
        setEntries((prev) => [...prev, { id, kind: 'command', cmd: fullCmd, cmdDisplay: fullCmd, output: { kind: output.kind, text: output.text, links: output.links } }])
    }

    function appendMotd() {
        const id = ++entryKeyRef.current
        setEntries((prev) => [...prev, { id, kind: 'motd', text: t('HomeBody') }])
    }

    function lsName(item) {
        if (item.cmd === 'whoami') return 'whoami*'
        const parts = item.cmd.split(' ')
        return parts[parts.length - 1]
    }

    function appendLsEntry(cmdText) {
        const id = ++entryKeyRef.current
        const listing = SESSION.map(lsName).sort().join('  ')
        setEntries((prev) => [...prev, { id, kind: 'command', cmd: 'ls', cmdDisplay: cmdText || 'ls', output: { kind: 'text', text: listing } }])
    }

    function appendHelp(cmdText) {
        const id = ++entryKeyRef.current
        const remaining = SESSION.filter((item) => !revealedRef.current[item.id])
        const text = remaining.length
            ? t('TerminalHelpIntro') + '\n' + remaining.map((i) => '  ' + i.cmd).join('\n') + '\n  clear — ' + t('TerminalHelpClearHint')
            : t('TerminalHelpNoneLeft')
        setEntries((prev) => [...prev, { id, kind: 'command', cmd: 'help', cmdDisplay: cmdText || 'help', output: { kind: 'help', text } }])
    }

    function appendError(cmdText) {
        const id = ++entryKeyRef.current
        setEntries((prev) => [...prev, { id, kind: 'command', cmd: cmdText, cmdDisplay: cmdText, output: { kind: 'error', text: t('TerminalErrorNotFound', { cmd: cmdText }) } }])
    }

    function appendEasterEgg(cmdText) {
        const id = ++entryKeyRef.current
        setEntries((prev) => [...prev, { id, kind: 'command', cmd: EASTER_EGG_CMD, cmdDisplay: cmdText || EASTER_EGG_CMD, output: { kind: 'easter', text: t('TerminalEasterEgg') } }])
    }

    async function revealInitial(ids) {
        const pending = (ids || []).filter((id) => !revealedRef.current[id])
        if (!pending.length) return
        setBusy(true)
        try {
            for (const id of pending) {
                const item = SESSION.find((s) => s.id === id)
                if (!item || revealedRef.current[item.id]) continue
                await appendCommandEntry(item)
                if (!mountedRef.current) return
                setRevealed((prev) => ({ ...prev, [item.id]: true }))
            }
        } finally {
            if (mountedRef.current) {
                setBusy(false)
                if (inputRef.current) inputRef.current.focus()
            }
        }
    }

    async function runCommand(raw) {
        const cmdText = raw.trim()
        if (!cmdText) return
        setHistoryList((prev) => [...prev, cmdText])
        setHistoryIdx(-1)
        setBusy(true)
        try {
            const lower = cmdText.toLowerCase()
            if (lower === 'clear') { setEntries([]); return }
            if (lower === 'help') { appendHelp(cmdText); return }
            if (lower === 'ls') { appendLsEntry(cmdText); return }
            if (lower === EASTER_EGG_CMD) { appendEasterEgg(cmdText); return }

            const match = SESSION.find((item) => item.cmd.toLowerCase() === lower)
            if (!match) { appendError(cmdText); return }

            await appendCommandEntry(match)
            if (mountedRef.current) setRevealed((prev) => ({ ...prev, [match.id]: true }))
        } finally {
            if (mountedRef.current) {
                setBusy(false)
                if (inputRef.current) inputRef.current.focus()
            }
        }
    }

    async function runAutoLoop() {
        const gen = ++autoGenRef.current
        const isCancelled = () => autoGenRef.current !== gen
        setLiveCmd('')
        setAutoDone(false)
        await wait(700)

        await typeLiveCommand('ls', isCancelled)
        if (isCancelled() || !mountedRef.current) return
        appendLsEntry()
        setLiveCmd('')
        await wait(1000)

        for (const item of SESSION) {
            if (isCancelled() || !mountedRef.current) return
            await typeLiveCommand(item.cmd, isCancelled)
            if (isCancelled() || !mountedRef.current) return
            await appendCommandEntry(item, isCancelled)
            if (isCancelled() || !mountedRef.current) return
            setRevealed((prev) => ({ ...prev, [item.id]: true }))
            setLiveCmd('')
            await wait(1400)
        }
        if (isCancelled() || !mountedRef.current) return
        setAutoDone(true)
    }

    // boot sequence — runs once
    useEffect(() => {
        let cancelled = false
        async function boot() {
            const lines = [t('TerminalBootLine1'), t('TerminalBootLine2'), t('TerminalBootLine3')]
            for (const line of lines) {
                if (cancelled) return
                setBootLines((prev) => [...prev, line])
                await wait(reducedMotion ? 0 : 380)
            }
            if (cancelled) return
            setBooted(true)
        }
        boot()
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // print welcome message + directory listing + how-to once booted (auto mode types its own "ls")
    useEffect(() => {
        if (!booted) return
        appendMotd()
        if (mode !== 'auto') {
            appendLsEntry()
            appendHelp()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booted])

    // drive auto-loop / focus input based on mode
    useEffect(() => {
        if (!booted) return
        if (mode === 'auto') {
            runAutoLoop()
        } else if (inputRef.current) {
            inputRef.current.focus()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booted, mode])

    // reveal deep-linked commands in guided mode
    useEffect(() => {
        if (!booted || mode !== 'guided') return
        revealInitial(initialCommands)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booted, mode, JSON.stringify(initialCommands)])

    function switchMode(nextMode) {
        if (nextMode === mode) return
        autoGenRef.current++
        setEntries([])
        setRevealed({})
        setHistoryList([])
        setHistoryIdx(-1)
        setBusy(false)
        setLiveCmd('')
        setAutoDone(false)
        setModeState(nextMode)
        try { localStorage.setItem(MODE_STORAGE_KEY, nextMode) } catch (e) { /* localStorage unavailable */ }
    }

    function restart() {
        autoGenRef.current++
        setEntries([])
        setRevealed({})
        setHistoryList([])
        setHistoryIdx(-1)
        setBusy(false)
        setLiveCmd('')
        setAutoDone(false)
        if (mode === 'auto') {
            runAutoLoop()
        } else {
            revealInitial(initialCommands)
            if (inputRef.current) inputRef.current.focus()
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            const val = inputValue
            setInputValue('')
            runCommand(val)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (!historyList.length) return
            setHistoryIdx((prev) => {
                const next = prev === -1 ? historyList.length - 1 : Math.max(0, prev - 1)
                setInputValue(historyList[next] || '')
                return next
            })
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!historyList.length) return
            setHistoryIdx((prev) => {
                if (prev === -1) return -1
                const next = prev + 1
                if (next >= historyList.length) { setInputValue(''); return -1 }
                setInputValue(historyList[next] || '')
                return next
            })
        } else if (e.key === 'Tab') {
            e.preventDefault()
            const typed = inputValue.trim().toLowerCase()
            if (!typed) return
            const remaining = SESSION.filter((item) => !revealed[item.id]).map((i) => i.cmd)
            const candidates = remaining.concat(['help', 'clear', 'ls']).filter((c) => c.toLowerCase().indexOf(typed) === 0)
            if (candidates.length === 1) setInputValue(candidates[0])
        }
    }

    const isAuto = mode === 'auto'
    const allRevealed = SESSION.every((item) => revealed[item.id])

    return (
        <div className="page">
            <header className="hero">
                <span className="eyebrow">{t('TerminalEyebrow')}</span>
                <h1 className="display">{t('TerminalHeroTitle')}</h1>
                <p className="lede">{t('TerminalLede')}</p>
            </header>

            <div className="mode-card">
                <div className="mode-copy">
                    <p className="mode-label">{t('TerminalModeLabel')}</p>
                    <p>{t(isAuto ? 'TerminalModeDescAuto' : 'TerminalModeDescGuided')}</p>
                </div>
                <div className="switch-wrap">
                    <span
                        className={'switch-side' + (!isAuto ? ' active' : '')}
                        onClick={() => switchMode('guided')}
                    >
                        {t('TerminalModeGuidedName')}
                    </span>
                    <button
                        type="button"
                        className="mode-switch"
                        role="switch"
                        aria-checked={isAuto}
                        aria-label={t('TerminalModeLabel')}
                        onClick={() => switchMode(isAuto ? 'guided' : 'auto')}
                    >
                        <span className="thumb"></span>
                    </button>
                    <span
                        className={'switch-side' + (isAuto ? ' active' : '')}
                        onClick={() => switchMode('auto')}
                    >
                        {t('TerminalModeAutoName')}
                    </span>
                </div>
            </div>

            <div className="window">
                <div className="titlebar">
                    <div className="titlebar-left">
                        <span className="wm-accent"></span>
                        <span className="titlebar-label">{PROMPT_USER} — zsh</span>
                    </div>
                    <div className="titlebar-right">
                        <span className="status-pill">
                            <span className={'status-dot' + (isAuto ? ' rec' : '')}></span>
                            {t(isAuto ? 'TerminalStatusPlaying' : 'TerminalStatusInteractive')}
                        </span>
                        <button type="button" className="restart-btn" onClick={restart}>{t('TerminalRestart')}</button>
                    </div>
                </div>

                <div className="window-body">
                    {!booted && (
                        <div className="boot-lines">
                            {bootLines.map((line, i) => (
                                <p key={i} className="term-out">{line}</p>
                            ))}
                            <span className="cursor" aria-hidden="true"></span>
                        </div>
                    )}

                    {booted && (
                        <React.Fragment>
                            <div className="scrollback">
                                {entries.map((entry) => {
                                    if (entry.kind === 'command') {
                                        return (
                                            <div className="out-block" key={entry.id}>
                                                <p className="term-prompt">
                                                    <span className="p-user">{PROMPT_USER}</span>
                                                    <span className="p-path">~</span>
                                                    <span className="p-arrow">❯</span>
                                                    <span className="p-cmd">{entry.cmdDisplay}</span>
                                                </p>
                                                {entry.output && entry.output.kind === 'loading' && (
                                                    <p className="term-out">{entry.output.text}</p>
                                                )}
                                                {entry.output && entry.output.kind === 'badges' && (
                                                    <div className="badge-row">
                                                        {entry.output.badges.map((b, i) => (
                                                            <span
                                                                key={i}
                                                                className="badge"
                                                                style={{
                                                                    background: b.bg,
                                                                    color: b.fg,
                                                                    border: b.border ? '1px solid #414868' : 'none',
                                                                    animationDelay: (i * 45) + 'ms'
                                                                }}
                                                            >
                                                                {b.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {entry.output && entry.output.kind === 'whoami' && (
                                                    <div className="whoami-row">
                                                        <img className="term-avatar" src={profileImg} alt="" />
                                                        <p className="term-out">{entry.output.text}</p>
                                                    </div>
                                                )}
                                                {entry.output && entry.output.kind === 'text' && (
                                                    <p className="term-out">{entry.output.text}</p>
                                                )}
                                                {entry.output && entry.output.kind === 'links' && (
                                                    <p className="term-out">{linkifyToReact(entry.output.text, entry.output.links)}</p>
                                                )}
                                                {entry.output && entry.output.kind === 'help' && (
                                                    <p className="term-help">{entry.output.text}</p>
                                                )}
                                                {entry.output && entry.output.kind === 'error' && (
                                                    <p className="term-err">{entry.output.text}</p>
                                                )}
                                                {entry.output && entry.output.kind === 'easter' && (
                                                    <p className="term-out term-easter">{entry.output.text}</p>
                                                )}
                                            </div>
                                        )
                                    }
                                    if (entry.kind === 'motd') return <p key={entry.id} className="term-motd">{entry.text}</p>
                                    return null
                                })}
                            </div>

                            {mode === 'guided' ? (
                                <React.Fragment>
                                    {allRevealed && <p className="term-help">{t('TerminalSessionComplete')}</p>}
                                    <div className="guided-row">
                                        <span className="p-user">{PROMPT_USER}</span>
                                        <span className="p-path">~</span>
                                        <span className="p-arrow">❯</span>
                                        <span className="input-shell" onClick={() => inputRef.current && inputRef.current.focus()}>
                                            <span className="input-text">{inputValue}</span>
                                            <span className="cursor" aria-hidden="true"></span>
                                            <input
                                                ref={inputRef}
                                                className="input-hidden"
                                                type="text"
                                                value={inputValue}
                                                onChange={(e) => setInputValue(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                disabled={busy}
                                                autoComplete="off"
                                                autoCapitalize="off"
                                                spellCheck="false"
                                                aria-label={t('TerminalInputPlaceholder')}
                                            />
                                        </span>
                                    </div>
                                    <p className="term-hint">{t('TerminalHint')}</p>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {autoDone && <p className="term-help">{t('TerminalSessionComplete')}</p>}
                                    <p className="term-prompt auto-row">
                                        <span className="p-user">{PROMPT_USER}</span>
                                        <span className="p-path">~</span>
                                        <span className="p-arrow">❯</span>
                                        <span className="p-cmd">{liveCmd}</span>
                                        <span className="cursor" aria-hidden="true"></span>
                                    </p>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    )
}

export default React.memo(Terminal, (prev, next) => JSON.stringify(prev.initialCommands) === JSON.stringify(next.initialCommands))
